import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { InmateApi, UserApi } from '@/api';
import { formatDate } from '@/components/DataGrid/utils';
import { Button, SidePaneModal } from '@/components/TailwindComponents';
import { IUser, Role } from '@/models';
import { NotesForm } from '@/pages/JMS/Managament/components/forms/formFields/notes';
import { renderFormField } from '@/pages/JMS/Managament/components/forms/formRender';
import { notesSchema } from '@/pages/JMS/Managament/components/forms/schemas/notesSchema';
import { IInmateNote } from '@/pages/JMS/Managament/types/inmate';
import { useBookingStore } from '@/pages/JMS/store/BookingStore';
import { useToastStore, useUserStore } from '@/store';
import { formatPersonName } from '@/utils';

type OfficerOption = { value: string; label: string };

interface IAddInmateNoteSidePanelProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setRefreshKey?: Dispatch<SetStateAction<number>>;
  currentNote?: IInmateNote | null;
  setCurrentNote?: Dispatch<SetStateAction<IInmateNote | null>>;
  inmateId?: string;
}

const AddInmateNoteSidePanel = ({
  setCurrentNote,
  currentNote,
  setRefreshKey,
  isOpen,
  setIsOpen,
  inmateId,
}: IAddInmateNoteSidePanelProps) => {
  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const { booking } = useBookingStore();

  const [officers, setOfficers] = useState<OfficerOption[]>([]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(notesSchema) as any,
    defaultValues: {
      description: '',
      officer: user?._id,
    },
  });

  const onSubmit = async (data: any) => {
    try {
      await InmateApi.createNote(data, inmateId || booking?.inmate?._id);
      setRefreshKey?.((prev: number) => prev + 1);
      reset();
      setIsOpen(false);
    } catch (e) {
      updateToast({ open: true, message: 'Something went wrong' });
    }
  };

  const fetchOfficers = useCallback(async () => {
    try {
      const { data } = await UserApi.get({
        limit: 1000,
        filter: JSON.stringify({ 'roles.role': Role.JAIL_OFFICER }),
      });

      const officersMap: OfficerOption[] = data.results.map((u: any) => ({
        value: u._id,
        label: formatPersonName(u),
      }));

      setOfficers(officersMap);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  }, [updateToast]);

  useEffect(() => {
    fetchOfficers();
  }, []);

  return (
    <SidePaneModal
      title={!currentNote ? 'Add Note' : 'Note'}
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          // Wait for animation to finish
          setTimeout(() => {
            setCurrentNote?.(null);
          }, 300);
        }
      }}
    >
      {!currentNote ? (
        <SidePaneModal.Content>
          <form
            onSubmit={handleSubmit(onSubmit)}
            id="notes"
            className="flex flex-col justify-between h-full px-1 space-y-4"
          >
            <div>
              {NotesForm.map(({ title, name, items }) => (
                <div
                  key={title}
                  className="space-y-4 bg-white rounded-lg shadow-sm"
                >
                  {items.map((field) =>
                    renderFormField({
                      field,
                      control: control as any,
                      errors,
                      name,
                      getOptionItems: {
                        getUsers: officers,
                      },
                    }),
                  )}
                </div>
              ))}
            </div>
          </form>
        </SidePaneModal.Content>
      ) : null}

      {currentNote ? (
        <SidePaneModal.Content>
          <div className="flex flex-col gap-3">
            <div className="break-words">
              <div className="text-[12px]">Note Description:</div>
              <div className="font-semibold text-[14px]">
                {currentNote.description}
              </div>
            </div>
            <div>
              <div className="text-[12px]">Officer:</div>
              <div className="font-semibold text-[14px]">
                {(currentNote?.officer as IUser)?.fullName}
              </div>
            </div>
            <div>
              <div className="text-[12px]">Created by:</div>
              <div className="font-semibold text-[14px]">
                {(currentNote?.createdBy as IUser)?.fullName}
              </div>
            </div>
            <div>
              <div className="text-[12px]">Created date:</div>
              <div className="font-semibold text-[14px]">
                {formatDate(currentNote?.createdAt as string)}
              </div>
            </div>
          </div>
        </SidePaneModal.Content>
      ) : null}
      {!currentNote ? (
        <SidePaneModal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button type="submit" form="notes">
            Save
          </Button>
        </SidePaneModal.Footer>
      ) : null}
    </SidePaneModal>
  );
};

export default AddInmateNoteSidePanel;
